import { useEffect, useRef } from "react";
import { trackReachedBooking, trackSchedule } from "../lib/pixel";
import { newEventId } from "../lib/lead";

interface Props {
  /** Para dedup con un Schedule server-side futuro (Fase 2, webhook de GHL). */
  value?: number;
  /** Título/subcopy contextual. */
  headline?: string;
  /** Oculta el encabezado propio (cuando el contenedor ya provee el CTA). */
  hideHeader?: boolean;
}

const BOOKING_URL = import.meta.env.VITE_GHL_BOOKING_URL as string | undefined;

/**
 * Paso de agendar (calendario) — el final del embudo.
 * - Al MOSTRARSE dispara InitiateCheckout (HOT: llegó al paso previo a agendar).
 * - Al AGENDAR dispara Schedule (REAL: agendó la cita).
 *
 * Si VITE_GHL_BOOKING_URL está configurado, embebe el calendario de GHL y escucha
 * el postMessage de reserva. Si no (aún Fase 1), muestra un fallback elegante — el
 * evento HOT igual se registra; Schedule se activa cuando el calendario de GHL exista.
 */
export default function BookingStep({ value, headline, hideHeader }: Props) {
  const scheduleId = useRef(newEventId());
  const booked = useRef(false);

  useEffect(() => {
    // HOT — llegó a la vista de agendar.
    trackReachedBooking(scheduleId.current, value);

    // REAL — escucha la confirmación de reserva del embed de GHL (LeadConnector).
    function onMessage(e: MessageEvent) {
      const d = e.data;
      const isBooking =
        (typeof d === "string" && /appointment|booked|schedule/i.test(d)) ||
        (d && typeof d === "object" &&
          /appointment|booked|schedule/i.test(JSON.stringify(d)));
      if (isBooking && !booked.current) {
        booked.current = true;
        trackSchedule(scheduleId.current, value);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [value]);

  return (
    <section className={hideHeader ? "" : "mt-10 border-t border-stone/15 pt-8"}>
      {!hideHeader && (
        <>
          <h2 className="font-serif text-2xl text-ink">
            {headline ?? "Book your design consultation"}
          </h2>
          <p className="mt-2 text-stone">
            Pick a time — <strong>Ana Clara and Javier</strong> walk your space themselves,
            the same couple who will design <em>and</em> build it.
          </p>
        </>
      )}

      {BOOKING_URL ? (
        <iframe
          title="Book your in-home design visit"
          src={BOOKING_URL}
          className="h-[680px] w-full rounded-xl border border-stone/20"
        />
      ) : (
        <div className="rounded-2xl bg-white/70 p-5 text-center text-sm text-stone">
          ✓ Your details are in. <strong>Ana Clara or Javier</strong> will reach out
          personally to find a time to walk your space.
        </div>
      )}
    </section>
  );
}
